import type { ExternalWorkspacePayload, Workspace } from "./types.js";

/** Exactly the parts of `cyborg:auth_response` the workspace list is built from. */
export interface AuthWorkspacesPayload {
  // `role` is NULLABLE on the wire (CYBORG-954): the server sends null for anything that is
  // not one of the four real roles, so the presence-row marker can never reach a role chip.
  workspaces: { id: string; name: string; avatarUrl: string | null; role: string | null }[];
  externalWorkspaces?: ExternalWorkspacePayload[];
}

/**
 * Cyborg7 Connect §8.7.3 — `workspaceState.list` as an auth response defines it.
 *
 * ONE BUILDER, because there are TWO `connectToServer`s (core/state.svelte.ts and
 * state/app.svelte.ts, the latter being the `?server=&token=` login). Each used to
 * hand-map the payload, the externals were added to only one, and an accepted
 * external logging in through the other was bounced straight back out of the host
 * workspace. Two hand-maintained copies of one list is the bug; this is the fix.
 *
 * WHY EXTERNALS SHARE THE LIST. `list` is what /workspace/[id]/+layout.svelte looks
 * the route's workspace up in, and a miss is `goto("/workspace")`. Externals are
 * deliberately not a `memberships` row (156 membership checks depend on that staying
 * true), so they can never arrive through `authResp.workspaces`.
 *
 * MEMBERSHIPS ARE STILL FIRST, so `list[0]` and every "my home workspace" read keeps
 * naming a workspace the person actually belongs to.
 *
 * A DISTINCT, ROLE-LESS KIND: `role` is null and `external` is set. Nothing may read
 * this as a weak member — every role-gated surface renders the not-available state
 * instead (ExternalNotAvailable), and the server refuses it regardless.
 */
export function workspaceListFromAuth(authResp: AuthWorkspacesPayload): Workspace[] {
  return [
    ...authResp.workspaces.map((ws) => ({
      id: ws.id,
      name: ws.name,
      ownerId: "",
      avatarUrl: ws.avatarUrl ?? null,
      role: ws.role as Workspace["role"],
      createdAt: 0,
      settings: {},
    })),
    ...(authResp.externalWorkspaces ?? []).map(externalWorkspaceEntry),
  ];
}

/**
 * THE one mapping from a wire `ExternalWorkspacePayload` to a list entry. Not
 * exported: the accept path reaches it through `upsertExternalWorkspace`, so there
 * is no second copy of this shape to drift from the auth path's.
 */
function externalWorkspaceEntry(ws: ExternalWorkspacePayload): Workspace {
  return {
    id: ws.id,
    name: ws.name,
    ownerId: "",
    avatarUrl: ws.avatarUrl ?? null,
    role: null,
    external: { orgName: ws.orgName, channelIds: ws.channelIds },
    createdAt: 0,
    settings: {},
  };
}

/**
 * Seat a freshly accepted Connect invitation in the list, before navigating into it.
 *
 * `cyborg:accept_external_invite` is the ONLY moment a host workspace becomes
 * reachable without a re-auth, and the accept page navigates into it immediately —
 * so without this the invitee's very first hop lands on a list that has never heard
 * of the workspace and /workspace/[id]/+layout.svelte bounces them back out. Every
 * real invitee takes that hop; signing in again was the only way through.
 *
 * The payload is SERVER-BUILT (the relay answers the accept with the same entry
 * cyborg:auth would carry, from the same two queries), so the client never invents
 * an org name or a wider set of channels than the socket was scoped to.
 *
 * IDEMPOTENT by id: a double accept, or an account already holding this connection,
 * replaces the entry rather than adding a second one. An existing MEMBERSHIP wins —
 * a real role must never be downgraded to a role-less external by an accept.
 */
export function upsertExternalWorkspace(
  current: Workspace[],
  ws: ExternalWorkspacePayload,
): Workspace[] {
  const existing = current.find((w) => w.id === ws.id);
  if (existing && !existing.external) return current;
  const entry = externalWorkspaceEntry(ws);
  return existing ? current.map((w) => (w.id === ws.id ? entry : w)) : [...current, entry];
}

/**
 * A memberships-only refresh, folded into the list WITHOUT evicting externals.
 *
 * `cyborg:fetch_workspaces` answers memberships and nothing else — externals are
 * deliberately not a `memberships` row — so assigning its result straight onto
 * `workspaceState.list` silently drops every host workspace the viewer reaches as
 * an external, and the shell bounces them out of it again. Same bug as the two
 * connectToServer's had, one refresh later.
 *
 * Memberships stay first, and an external that has since become a real membership
 * is kept once, as the membership.
 */
export function mergeMembershipRefresh(
  current: Workspace[],
  memberships: Workspace[],
): Workspace[] {
  const refreshed = new Set(memberships.map((w) => w.id));
  return [...memberships, ...current.filter((w) => w.external && !refreshed.has(w.id))];
}

/** Find the route's workspace, re-reading the server once on a miss (joined elsewhere). */
export async function findOrRefreshWorkspace(
  id: string,
  list: () => Workspace[],
  refresh: () => Promise<void>,
): Promise<Workspace | undefined> {
  const hit = list().find((w) => w.id === id);
  if (hit) return hit;
  await refresh();
  return list().find((w) => w.id === id);
}

/**
 * The list after a re-auth: the auth answer decides WHICH workspaces and of which kind (a host
 * can become a membership, or a membership an external, while the socket was down); an entry
 * of the same id and kind keeps what was hydrated since (settings, a resolved role).
 */
export function reconcileWorkspaceList(
  current: Workspace[],
  authResp: AuthWorkspacesPayload,
): Workspace[] {
  const next: Workspace[] = [];
  for (const fresh of workspaceListFromAuth(authResp)) {
    const held = current.find((w) => w.id === fresh.id && !w.external === !fresh.external);
    next.push(
      held
        ? {
            ...held,
            name: fresh.name,
            avatarUrl: fresh.avatarUrl,
            role: fresh.role ?? held.role,
            external: fresh.external,
          }
        : fresh,
    );
  }
  return next;
}

/** My memberships: a background fan-out of member-only reads iterates these, since the relay
 * refuses every one of them (bootstrap, fetch_unread, list_members…) for a host I reach as an external. */
export function memberWorkspaces(list: Workspace[]): Workspace[] {
  return list.filter((w) => !w.external);
}

/** A host I reach ONLY as an external (a membership of the same id wins). */
export function isOutsiderSeat(list: Workspace[], workspaceId: string): boolean {
  const entries = list.filter((w) => w.id === workspaceId);
  return entries.length > 0 && entries.every((w) => w.external);
}

// connect-external.ts's OUTSIDER_REACHABLE_RPCS + the pre-floor subscribe (pinned by a test).
export const OUTSIDER_REACHABLE_RPCS: ReadonlySet<string> = new Set([
  "cyborg:subscribe_workspace",
  "cyborg:channel_message",
  "cyborg:dm",
  "cyborg:typing",
  "cyborg:fetch_dm_messages",
  "cyborg:fetch_dm_since",
  "cyborg:mark_dm_read",
  "cyborg:fetch_channels",
  "cyborg:fetch_messages",
  "cyborg:fetch_channel_cybos",
  "cyborg:search",
  "cyborg:mark_read",
  "cyborg:set_notification_pref",
  "cyborg:fetch_notification_prefs",
  "cyborg:set_channel_archived_for_me",
  "cyborg:remove_channel_member",
  "cyborg:fetch_thread",
  "cyborg:fetch_message",
  "cyborg:fetch_activity",
]);

// What a switcher offers: not hosts reached as an external (the External section is their way
// in), unless the account has no membership of its own.
export function switcherWorkspaces(list: Workspace[]): Workspace[] {
  const own = memberWorkspaces(list);
  return own.length > 0 ? own : list;
}

// Where a launch lands: the last workspace (an external host too), else the only one offered.
export function launchWorkspace(list: Workspace[], lastId: string | null): Workspace | null {
  const offered = switcherWorkspaces(list);
  return list.find((w) => w.id === lastId) ?? (offered.length === 1 ? offered[0] : null); // null: show the picker
}
